import { useEffect, useRef, useState } from "react";

import {
  AMPLI_DISBURSEMENT_SOURCE,
  ampliInconsistentReasonLabel,
  cashFlowPlanningSourceLabel,
  isAmpliDisbursementPolicy,
  physicalFinancialLabel,
} from "./cashFlowPlanning";
import { updateCashFlowPlanningSource } from "./cashFlowPlanningApi";

const AMPLI_DISABLED_REASON =
  "Requer previsto canônico na obra (físico-financeiro Sienge ou snapshot Prevision)";

const STATE_BADGE = Object.freeze({
  selection_required: "Escolha pendente",
  needs_adjustment: "Em ajuste",
  unavailable: "Indisponível",
  not_applicable: "Indisponível",
  inconsistent: "Inconsistente",
  stale: "Desatualizada",
});

// Uma fonte ainda não escolhida é um passo de configuração, não um estado de rotina: o
// gatilho recolhido não tem o que resumir, então o popover começa aberto. Os demais
// estados de atenção continuam recolhidos, sinalizados no próprio gatilho.
const OPEN_BY_DEFAULT = new Set(["selection_required"]);
const NEEDS_ATTENTION = new Set([
  "selection_required",
  "needs_adjustment",
  "unavailable",
  "not_applicable",
  "inconsistent",
  "stale",
]);

// A curva Ampli chega "stale" via `condition`, não `state` (o BE reporta
// `state: "ready", condition: "stale"` para uma curva desatualizada mas ainda servível).
// `state === "stale"` é tolerado defensivamente caso o BE mude de forma no futuro.
const isStaleCondition = (policy) =>
  policy?.condition === "stale" || policy?.state === "stale";

const badgeFor = (policy) => {
  if (isStaleCondition(policy)) return STATE_BADGE.stale;
  return STATE_BADGE[policy?.state] ?? "Atenção";
};

const copyFor = (policy) => {
  if (isAmpliDisbursementPolicy(policy) && policy?.state === "inconsistent") {
    return {
      title: "Desembolso Ampli: última geração recusada",
      description: `${ampliInconsistentReasonLabel(policy.reason)} Nenhuma curva nova foi publicada; ajuste as regras e gere de novo.`,
    };
  }
  if (isAmpliDisbursementPolicy(policy) && isStaleCondition(policy)) {
    return {
      title: "Desembolso Ampli: curva sendo atualizada",
      description:
        "A curva anterior continua no ar enquanto a nova é gerada. Recarregue em instantes para ver os valores atualizados.",
    };
  }
  if (isAmpliDisbursementPolicy(policy) && policy?.state === "unavailable") {
    return {
      title: "Desembolso Ampli indisponível nesta obra",
      description:
        "O desembolso Ampli precisa de um previsto canônico na obra (físico-financeiro do ERP ou snapshot Prevision aplicado).",
    };
  }
  if (policy?.state === "ready" && policy.effectiveSource === AMPLI_DISBURSEMENT_SOURCE) {
    return {
      title: "Previsão de caixa pelo desembolso Ampli",
      description:
        "A organização cadastra a forma de desembolso na Ampli e a plataforma redistribui o previsto no tempo. O previsto físico-financeiro continua visível ao lado.",
    };
  }
  if (
    policy?.requestedSource === "combined" &&
    policy?.state === "needs_adjustment"
  ) {
    return {
      title: "Visão completa: desembolso em ajuste",
      description: `${physicalFinancialLabel(policy)} continua disponível. O desembolso aparece em paralelo e mantém a jornada para chegar a 100% de cobertura.`,
    };
  }
  if (policy?.state === "needs_adjustment") {
    return {
      title: "Desembolso escolhido: conclua os ajustes",
      description:
        "Enquanto a cobertura não chegar a 100%, o desembolso não será usado silenciosamente como previsão de caixa.",
    };
  }
  if (policy?.state === "ready" && policy.effectiveSource === "disbursement") {
    return {
      title: "Previsão de caixa pelo cronograma de desembolso",
      description:
        "A organização usa o desembolso reconciliado como fonte canônica da projeção.",
    };
  }
  if (
    policy?.state === "ready" &&
    policy.effectiveSource === "physical_financial"
  ) {
    return {
      title: "Previsão de caixa pelo físico-financeiro",
      description:
        "A organização usa o previsto mensal e não precisa ajustar o cronograma de desembolso.",
    };
  }
  if (policy?.state === "ready" && policy.effectiveSource === "combined") {
    return {
      title: "Visão completa da previsão de caixa",
      description: `${physicalFinancialLabel(policy)} e o desembolso reconciliado aparecem juntos, sem soma ou mistura entre as curvas.`,
    };
  }
  if (policy?.state === "unavailable") {
    return {
      title: "Fonte de previsão temporariamente indisponível",
      description:
        "A fonte escolhida não possui snapshot utilizável. Nenhuma outra fonte foi aplicada como fallback.",
    };
  }
  return {
    title: "Como esta organização quer planejar os desembolsos?",
    description:
      "Escolha uma fonte única ou a visão completa. Na visão completa, previsto e desembolso permanecem identificados e nunca são somados.",
  };
};

const CashFlowPlanningSourceCard = ({ policy, projectId, onUpdated }) => {
  const [editing, setEditing] = useState(
    policy?.state === "selection_required",
  );
  const [pendingSource, setPendingSource] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(OPEN_BY_DEFAULT.has(policy?.state));
  const containerRef = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);
  if (!policy) return null;

  const copy = copyFor(policy);
  const showChoices =
    policy.canManage && (editing || policy.state === "selection_required");
  const needsAttention = NEEDS_ATTENTION.has(policy.state) || isStaleCondition(policy);

  const save = async () => {
    if (!pendingSource || saving) return;
    setSaving(true);
    setError(false);
    const result = await updateCashFlowPlanningSource(projectId, pendingSource);
    if (!mounted.current) return;
    if (!result) {
      setError(true);
      setSaving(false);
      return;
    }
    setSaving(false);
    setEditing(false);
    setPendingSource(null);
    await onUpdated?.(result.source);
  };

  return (
    <div className="cash-flow-planning-source-chip-wrapper" ref={containerRef}>
      <button
        type="button"
        className={`cash-flow-planning-source-chip cash-flow-planning-source-chip--${policy.state}${needsAttention ? " cash-flow-planning-source-chip--attention" : ""}`}
        aria-label="Política de desembolso da organização"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="cash-flow-planning-source-chip__label">Política</span>
        <strong>
          {policy.requestedSource
            ? cashFlowPlanningSourceLabel(policy.requestedSource)
            : "Não definida"}
        </strong>
        {needsAttention ? (
          <span className="cash-flow-planning-source-chip__condition">
            {badgeFor(policy)}
          </span>
        ) : null}
        <span aria-hidden="true" className="cash-flow-planning-source-chip__caret">
          {open ? "▲" : "▼"}
        </span>
      </button>
      {open ? (
      <section className={`cash-flow-planning-source ${policy.state}`}>
        <div className="cash-flow-planning-source__header">
          <div>
            <span className="coverage-eyebrow">Política da organização</span>
            <h3>{copy.title}</h3>
            <p>{copy.description}</p>
          </div>
          {policy.requestedSource ? (
            <span className="cash-flow-planning-source__badge">
              {cashFlowPlanningSourceLabel(policy.requestedSource)}
            </span>
          ) : null}
        </div>

        {!policy.canManage && policy.state === "selection_required" ? (
          <p className="cash-flow-planning-source__notice">
            Peça a um administrador da empresa para escolher a fonte da
            organização.
          </p>
        ) : null}

        {showChoices ? (
          <div className="cash-flow-planning-source__choices">
            <button
              type="button"
              aria-pressed={pendingSource === AMPLI_DISBURSEMENT_SOURCE}
              className={
                pendingSource === AMPLI_DISBURSEMENT_SOURCE ? "selected" : undefined
              }
              disabled={!policy.availableSources?.ampliDisbursement || saving}
              title={
                !policy.availableSources?.ampliDisbursement
                  ? AMPLI_DISABLED_REASON
                  : undefined
              }
              aria-describedby={
                !policy.availableSources?.ampliDisbursement
                  ? "cash-flow-planning-source-ampli-disabled-reason"
                  : undefined
              }
              onClick={() => setPendingSource(AMPLI_DISBURSEMENT_SOURCE)}
            >
              <strong>Usar desembolso Ampli</strong>
              <span>
                Cadastre a forma de pagar por família de insumo e a curva se
                redistribui sozinha, também para ERP sem cronograma de desembolso.
              </span>
              {!policy.availableSources?.ampliDisbursement ? (
                <span
                  id="cash-flow-planning-source-ampli-disabled-reason"
                  className="visually-hidden"
                >
                  {AMPLI_DISABLED_REASON}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              aria-pressed={pendingSource === "disbursement"}
              className={
                pendingSource === "disbursement" ? "selected" : undefined
              }
              disabled={!policy.availableSources?.disbursement || saving}
              onClick={() => setPendingSource("disbursement")}
            >
              <strong>Usar cronograma de desembolso</strong>
              <span>Exige 100% de cobertura e libera a jornada de ajustes.</span>
            </button>
            <button
              type="button"
              aria-pressed={pendingSource === "combined"}
              className={pendingSource === "combined" ? "selected" : undefined}
              disabled={!policy.availableSources?.combined || saving}
              onClick={() => setPendingSource("combined")}
            >
              <strong>Usar visão completa</strong>
              <span>
                Previsto e desembolso aparecem juntos, sem soma, com suas fontes identificadas.
              </span>
            </button>
            <button
              type="button"
              aria-pressed={pendingSource === "physical_financial"}
              className={
                pendingSource === "physical_financial" ? "selected" : undefined
              }
              disabled={!policy.availableSources?.physicalFinancial || saving}
              onClick={() => setPendingSource("physical_financial")}
            >
              <strong>Usar previsto físico-financeiro</strong>
              <span>
                Usa o previsto mensal e ignora pendências do desembolso.
              </span>
            </button>
          </div>
        ) : null}

        {showChoices && pendingSource ? (
          <div className="cash-flow-planning-source__confirm">
            <p>
              Esta escolha vale para todas as obras da organização:{" "}
              {cashFlowPlanningSourceLabel(pendingSource)}.
            </p>
            <div>
              {policy.requestedSource ? (
                <button
                  type="button"
                  className="button button-secondary small"
                  disabled={saving}
                  onClick={() => {
                    setPendingSource(null);
                    setEditing(false);
                  }}
                >
                  Cancelar
                </button>
              ) : null}
              <button
                type="button"
                className="button button-accent small"
                disabled={saving}
                onClick={save}
              >
                {saving ? "Salvando…" : "Confirmar fonte"}
              </button>
            </div>
          </div>
        ) : null}

        {policy.canManage && policy.requestedSource && !showChoices ? (
          <button
            type="button"
            className="cash-flow-planning-source__change"
            onClick={() => setEditing(true)}
          >
            Alterar fonte da organização
          </button>
        ) : null}

        {error ? (
          <p className="alert alert-error" role="alert">
            Não foi possível salvar a fonte. Tente novamente.
          </p>
        ) : null}
      </section>
      ) : null}
    </div>
  );
};

export default CashFlowPlanningSourceCard;
